import React from 'react';
import ReactDOM from 'react-dom';
import Amplify from 'aws-amplify';
import get from 'lodash/get';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { createBrowserHistory } from 'history';
import { loadableReady } from '@loadable/component';
import * as Sentry from '@sentry/browser';
import { configureStore } from '@ts/core/configureStore';
import { getScrollbarWidth } from '@ts/utils/browser';
import { AnalyticsInstance } from '@ts/utils/analytics';
import { makeCognitoConfig } from '@ts/utils/cognito';
import { setIsFirstRender } from '@ts/core/actions/app';
import { getSearchDefauls } from '@ts/utils/funcs';
import * as search from '@ts/core/actions/search';
import ErrorBoundary from '@ts/commons/ErrorBoundary';
import App from '@ts/App';

import * as Cookies from '@ts/utils/cs';
import appSaga from '@ts/core/sagas';
import { getToast } from './getToast';
import '@ts/utils/iterable';

const MOUNT_NODE = document.getElementById('app') as HTMLElement;
const history = createBrowserHistory();
window.browserHistory = history;

const env = process.env.NODE_ENV;
const isBuildProd = process.env.BUILD_ENV === 'prod';
const isProd = env === 'production';
const isLocalHost = window.location.hostname === 'localhost';
const isPRHost = window.location.hostname === 'test-front-end.tripshock.com';
const Port = window.location.port;
const isProdHost = window.location.hostname === 'www.tripshock.com';
const isStagingHost = window.location.hostname === 'staging-front-end.tripshock.com';

if (isProd) {
    // this ↓
    Sentry.init({
        dsn: 'https://508039440e2647ef976b18cfe8e16f4e@sentry.tripshock.com/3',
        debug: !isBuildProd,
        environment: isBuildProd ? 'prod' : 'dev',
    });
}

const store = configureStore({
    initialState: window.__PRELOADED_STATE__,
});

if (window.location.href.search('from=') === -1) {
    const defaults = getSearchDefauls();
    store.dispatch(search.changeDate(defaults.dateFrom, defaults.dateTo));
}

window.store = {
    getState: store.getState,
    getValue: (k: string) => get(store.getState(), k),
};

window.dataLayerTM = window.dataLayerTM || [];
window.fbqTM = window.fbqTM || function() {};
window._iaq = window._iaq || [];

window._iaq.push([
    'account',
    process.env.BUILD_ENV === 'prod'
        ? 'fb084b4971ca4a45996297a042520ebd'
        : '65617bc6915f49a9a8bae38aab965045',
]);

let appSagaTask: any = store.runSaga(appSaga);

window.tscookies = {
    get: Cookies.get,
    set: Cookies.set,
    CS_PROMOCODE: Cookies.CS_PROMOCODE,
    CS_SUBSCRIBE_TM: Cookies.CS_SUBSCRIBE_TM,
    CS_TOKEN_CART: Cookies.CS_TOKEN_CART,
    CS_DISABLE_ANALYTICS: Cookies.CS_DISABLE_ANALYTICS,
};

AnalyticsInstance.enableDebug(!isProdHost);

let config = null;

if (isLocalHost) {
    config = makeCognitoConfig({
        userPoolId: 'us-east-1_riLYj4qA1',
        userPoolWebClientId: '35q07tlb9c3ej584j0lprbe8na',
        domain: 'staging-auth.tripshock.com',
        redirectUrl: 'http://localhost:8500/',
    });
}

if (isPRHost) {
    config = makeCognitoConfig({
        userPoolId: 'us-east-1_riLYj4qA1',
        userPoolWebClientId: '35q07tlb9c3ej584j0lprbe8na',
        domain: 'staging-auth.tripshock.com',
        redirectUrl: `https://test-front-end.tripshock.com:${Port}/`,
    });
}

if (isStagingHost) {
    config = makeCognitoConfig({
        userPoolId: 'us-east-1_riLYj4qA1',
        userPoolWebClientId: '35q07tlb9c3ej584j0lprbe8na',
        domain: 'staging-auth.tripshock.com',
        redirectUrl: 'https://staging-front-end.tripshock.com/',
    });
}

if (isProdHost) {
    config = makeCognitoConfig({
        userPoolId: 'us-east-1_kuORepUGA',
        userPoolWebClientId: '27et3uh7gld1cbc36prj1admcq',
        domain: 'auth.tripshock.com',
        redirectUrl: 'https://www.tripshock.com/',
    });
}

Amplify.configure(config);

const hydrate = () =>
    ReactDOM.hydrate(
        <Provider store={store}>
            <Router history={history}>
                <HelmetProvider>
                    <ErrorBoundary>
                        <App />
                    </ErrorBoundary>
                </HelmetProvider>
            </Router>
        </Provider>,
        MOUNT_NODE
    );

function enableHotReloading() {
    // Hot reloadable React components and translation json files
    // modules.hot.accept does not accept dynamic dependencies,
    // have to be constants at compile-time
    if (module.hot) {
        module.hot.accept(['@ts/App'], () => {
            ReactDOM.unmountComponentAtNode(MOUNT_NODE);
            hydrate();
        });

        module.hot.accept('@ts/core/sagas', () => {
            if (appSagaTask) {
                appSagaTask.cancel();
                appSagaTask
                    .toPromise()
                    .then(() => import('@ts/core/sagas').then((m) => m.default))
                    .then((appSaga: any) => (appSagaTask = store.runSaga(appSaga)));
            }
        });
    }
}

async function asyncStartApplication() {
    // intersection-observer polyfill for IE11
    if (typeof window.IntersectionObserver === 'undefined') {
        await import('intersection-observer');
    }

    try {
        await loadableReady();
        hydrate();
        store.dispatch(setIsFirstRender(false));
    } catch {}

    try {
        await import('picturefill');

        if (getScrollbarWidth() <= 0 && document.body) {
            document.body.classList.add('body--mr0--important');
        }
    } catch {}

    if (!isProd) {
        enableHotReloading();
    }
}

asyncStartApplication();

(window as any).getToast = getToast;
